import { useMutation, useQueryClient } from '@tanstack/react-query';
import API from '../../../../context/API';
import { requireActiveStoreId } from '../../../../utils/activeStore';
import { CustomizationData } from '../types';
import { CUSTOMIZATIONS_KEY } from './useCustomizationsQuery';

const invalidate = (queryClient: ReturnType<typeof useQueryClient>) => () =>
  queryClient.invalidateQueries({ queryKey: [CUSTOMIZATIONS_KEY] });

const createCustomizationAPI = async (data: CustomizationData) => {
  const payload = {
    customization: data.customization?.trim() || '',
    options: [],
    note: data.note?.trim() || '',
    enable_quantity_control: Boolean(data.enable_quantity_control),
    min_quantity: data.enable_quantity_control ? (data.min_quantity ?? null) : null,
    max_quantity: data.enable_quantity_control ? (data.max_quantity ?? null) : null,
    option_limit: 0,
    multiple_selections: Boolean(data.multiple_selections),
    sold_out: false,
    multiple_selections_quantity: data.multiple_selections ? (data.multiple_selections_quantity ?? 0) : 0,
    related_items: [],
    storeId: requireActiveStoreId(),
  };
  const response = await API.post('/customizations', payload);
  if (response.status === 201 || response.data.success) {
    return response.data.data || response.data;
  }
  throw new Error('Failed to create customization.');
};

const updateCustomizationAPI = async ({ id, data, type }: { id: number; data: any; type: string }) => {
  // storeId isn't accepted in the update body — just confirm a store is
  // active before firing any of these requests.
  requireActiveStoreId();
  let response;
  const endpoint = `/customizations/${id}`;

  if (type === 'name') {
    try {
      response = await API.put(`/customizations/${id}/name`, { customization: data.customization.trim() });
    } catch {
      response = await API.put(endpoint, { customization: data.customization.trim() });
    }
  } else if (type === 'options') {
    const payload = {
      options: data.map((option: any) =>
        typeof option === 'object' && option?.id
          ? { id: option.id.toString(), sequence_number: option.sequence_number }
          : option.toString()
      ),
    };
    response = await API.put(`/customizations/${id}/options`, payload);
  } else if (type === 'addtoitems') {
    const payload = {
      customization: data.customization,
      options: Array.isArray(data.options)
        ? data.options.map((o: any) =>
            typeof o === 'object' && o?.id ? o.id.toString() : o.toString()
          )
        : [],
      note: data.note || '',
      max_quantity: data.max_quantity || 0,
      min_quantity: data.min_quantity || 0,
      option_limit: 0,
      multiple_selections: data.multiple_selections || false,
      sold_out: data.sold_out !== undefined ? Boolean(data.sold_out) : false,
      multiple_selections_quantity: data.multiple_selections_quantity || 0,
      related_items: (data.related_items || [])
        .map((ri: any, idx: number) => ({
          id: ri.item?.id ? ri.item.id.toString() : ri.id ? ri.id.toString() : '',
          sequence_number: ri.sequence_number || idx.toString(),
        }))
        .filter((item: any) => item.id !== ''),
      merchant: {},
    };
    response = await API.put(endpoint, payload);
  } else if (type === 'customizationrules') {
    const payload = {
      note: data.note || '',
      enable_quantity_control: Boolean(data.enable_quantity_control),
      min_quantity: data.enable_quantity_control ? (data.min_quantity ?? null) : null,
      max_quantity: data.enable_quantity_control ? (data.max_quantity ?? null) : null,
      option_limit: data.option_limit || 0,
      multiple_selections: Boolean(data.multiple_selections),
      multiple_selections_quantity: data.multiple_selections ? (data.multiple_selections_quantity || 0) : 0,
      merchant: data.merchant || {},
    };
    response = await API.put(`/customizations/${id}/rules`, payload);
  } else {
    response = await API.put(endpoint, data);
  }

  if (response.data.success) return response.data.data;
  throw new Error('Failed to update customization.');
};

const deleteCustomizationAPI = async (id: number) => {
  await API.delete(`/customizations/${id}`);
  return id;
};

const cloneCustomizationAPI = async (id: number) => {
  const storeId = requireActiveStoreId();
  const response = await API.get(`/customizations/clone/${id}?storeId=${storeId}`);
  if (response.data.success) return response.data.data;
  throw new Error('Failed to clone customization.');
};

const bulkUpdateOrderAPI = async (updates: { id: number; sequence_number: number }[]) => {
  requireActiveStoreId();
  const response = await API.put('/customizations/bulk-update', { updates });
  if (response.data.success) return updates;
  throw new Error('Failed to reorder customizations.');
};

const updateOptionSoldOutAPI = async ({ option, updatedOption }: { option: any; updatedOption: any }) => {
  requireActiveStoreId();
  await API.put(`/options/${option.id}`, {
    option: updatedOption.option,
    description: updatedOption.description,
    price: updatedOption.price,
    sequence_number: updatedOption.sequence_number,
    sold_out: updatedOption.sold_out,
    customization: updatedOption.customization,
  });
  return { optionId: option.id, sold_out: updatedOption.sold_out };
};

export const useCustomizationMutations = () => {
  const queryClient = useQueryClient();
  const inv = invalidate(queryClient);

  const createMutation = useMutation({
    mutationFn: createCustomizationAPI,
    onSuccess: inv,
  });

  const updateMutation = useMutation({
    mutationFn: updateCustomizationAPI,
    onSuccess: inv,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCustomizationAPI,
    onSuccess: inv,
  });

  const cloneMutation = useMutation({
    mutationFn: cloneCustomizationAPI,
    onSuccess: inv,
  });

  const bulkOrderMutation = useMutation({
    mutationFn: bulkUpdateOrderAPI,
  });

  const updateOptionSoldOutMutation = useMutation({
    mutationFn: updateOptionSoldOutAPI,
    onSuccess: inv,
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
    cloneMutation,
    bulkOrderMutation,
    updateOptionSoldOutMutation,
  };
};
